import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquareText, SquarePen, Trash2 } from 'lucide-react';
import { Modal } from '../../components/common/Modal';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { roasterService } from '../../services/roasterService';
import { getErrorMessage } from '../../utils/error';
import { RoasterForm } from './RoasterForm';
import { useToast } from '../../components/common/Toast';
import type { Roaster, RoasterFormData } from '../../types/roaster';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';

export const RoasterList: React.FC = () => {
  const { t } = useLanguage();
  const [roasters, setRoasters] = useState<Roaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const { showToast } = useToast();
  const { can } = usePermissions();
  const canWrite = can('roasters', 'write');
  const canDelete = can('roasters', 'delete');

  useEffect(() => {
    loadRoasters();
  }, []);

  const loadRoasters = async () => {
    try {
      setLoading(true);
      const data = await roasterService.getAll();
      setRoasters(data);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('production.roasterList.confirmDelete'))) return;
    try {
      await roasterService.delete(id);
      showToast(t('production.roasterList.toast.deleted'), 'success');
      loadRoasters();
    } catch (error) {
      showToast(getErrorMessage(error, t('production.roasterList.toast.deleteFailed')), 'error');
    }
  };

  const handleSave = async (data: RoasterFormData) => {
    try {
      if (editingId) {
        await roasterService.update(editingId, data);
        showToast(t('production.roasterList.toast.updated'), 'success');
      } else {
        await roasterService.create(data);
        showToast(t('production.roasterList.toast.created'), 'success');
      }
      setIsModalOpen(false);
      loadRoasters();
    } catch (err: unknown) {
      const rawMessage = getErrorMessage(err, t('production.roasterList.toast.saveFailed'));
      const message = ['duplicate', 'already exists', 'unique']
        .some((token) => rawMessage.toLowerCase().includes(token))
        ? t('production.roasterList.toast.duplicateName')
        : rawMessage;
      showToast(message, 'error');
    }
  };

  const filteredRoasters = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return roasters.filter(roaster => {
      const matchesSearch = query.length === 0 || roaster.name.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && roaster.isActive) ||
        (statusFilter === 'inactive' && !roaster.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [roasters, searchQuery, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('production.roasterList.title')}</h1>
        {canWrite && (
          <button
            data-test-id="roaster-add-button"
            onClick={handleCreate}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('production.roasterList.actions.add')}
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row flex-wrap gap-4">
        <div className="flex-1">
          <label htmlFor="roasterSearch" className="block text-sm font-medium text-gray-700">
            {t('purchasing.sampleList.filters.search')}
          </label>
          <input
            data-test-id="roaster-search-input"
            type="text"
            id="roasterSearch"
            placeholder={t('production.roasterList.searchPlaceholder')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
          />
        </div>
        <div className="w-full md:w-48">
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
          <select
            data-test-id="roaster-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="all">{t('salesOrder.list.status.all')}</option>
            <option value="active">{t('forms.common.statusActive')}</option>
            <option value="inactive">{t('forms.common.statusInactive')}</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-test-id="roaster-table">
        {loading ? (
          <LoadingSpinner text={t('production.roasterList.loading')} />
        ) : filteredRoasters.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('production.roasterList.empty')}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('forms.customer.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('forms.common.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('settingsMcp.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRoasters.map((roaster) => (
                <tr key={roaster.id} className="hover:bg-gray-50" data-test-id={`roaster-row-${roaster.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <span>{roaster.name}</span>
                      {roaster.notes && (
                        <div className="group relative" data-test-id={`roaster-notes-${roaster.id}`}>
                          <MessageSquareText className="h-4 w-4 text-blue-500" />
                          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200">
                            {roaster.notes}
                            <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {roaster.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('forms.common.statusActive')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {t('forms.common.statusInactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                    <button
                      data-test-id={`roaster-edit-button-${roaster.id}`}
                      onClick={() => handleEdit(roaster.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                    >
                      <SquarePen className="w-4 h-4" />
                      {canWrite ? t('purchasing.supplierList.actions.edit') : t('common.view')}
                    </button>
                    {canDelete && (
                      <button
                        data-test-id={`roaster-delete-button-${roaster.id}`}
                        onClick={() => handleDelete(roaster.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                      >
                        <Trash2 size={18} />
                        {t('purchasing.supplierList.actions.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t('production.roasterList.modal.editTitle') : t('production.roasterList.modal.addTitle')}
      >
        <RoasterForm
          initialData={roasters.find((r) => r.id === editingId)}
          onSubmit={handleSave}
          onCancel={() => setIsModalOpen(false)}
          readOnly={!canWrite}
        />
      </Modal>
    </div>
  );
};
